import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import PerfumeCard from "@/components/PerfumeCard";
import Reveal from "@/components/Reveal";

const QUESTIONS = [
  {
    key: "gender",
    title: "Pour qui cherchez-vous un parfum ?",
    options: [
      { v: "Femme", label: "Pour une femme" },
      { v: "Homme", label: "Pour un homme" },
      { v: "Mixte", label: "Mixte / unisexe" },
      { v: "any", label: "Peu importe" },
    ],
  },
  {
    key: "family",
    title: "Quel univers olfactif vous attire ?",
    options: [
      { v: "Gourmand", label: "Sucré — vanille, caramel, praline" },
      { v: "Frais", label: "Frais — agrumes, notes marines" },
      { v: "Boisé", label: "Boisé — cèdre, santal, cuir" },
      { v: "Oriental", label: "Oriental — ambre, épices" },
      { v: "Floral", label: "Floral — rose, jasmin, fleurs" },
    ],
  },
  {
    key: "occasion",
    title: "Pour quelle occasion ?",
    options: [
      { v: "Quotidien", label: "Tous les jours" },
      { v: "Soirée", label: "Soirée / sortie" },
      { v: "Rendez-vous", label: "Rendez-vous" },
    ],
  },
  {
    key: "budget",
    title: "Quel est votre budget ?",
    options: [
      { v: 30, label: "Moins de 30 €" },
      { v: 80, label: "30 € à 80 €" },
      { v: 130, label: "80 € à 130 €" },
      { v: 999, label: "Plus de 130 €" },
    ],
  },
];

const Quiz = () => {
  const [perfumes, setPerfumes] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.get("/perfumes").then((r) => setPerfumes(r.data)).catch(() => {});
  }, []);

  const answer = (key, value) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      const scored = perfumes
        .map((p) => {
          let score = 0;
          if (next.gender === "any" || p.gender === next.gender || p.gender === "Mixte") score += 2;
          if (p.family === next.family) score += 3;
          if ((p.occasions || []).includes(next.occasion)) score += 1.5;
          const price50 = Number(p.prices["50"]);
          if (price50 <= next.budget) score += 3;
          else if (Number(p.prices["30"]) <= next.budget) score += 1.5;
          if (!p.in_stock) score -= 0.5;
          return { p, score };
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, 3)
        .map((s) => s.p);
      setResults(scored);
    }
  };

  const restart = () => {
    setStep(0);
    setAnswers({});
    setResults(null);
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-8">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">Diagnostic olfactif</p>
        <h1 className="mt-2 font-serif text-3xl text-[#1A1714] sm:text-4xl">Trouver mon parfum idéal</h1>
        <p className="mt-3 text-sm text-[#5C5449]">4 questions, 3 parfums faits pour vous.</p>
      </Reveal>

      <div data-testid="perfume-quiz-container">
        {!results ? (
          <div className="mt-10">
            <div className="flex gap-1.5">
              {QUESTIONS.map((_, i) => (
                <span key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#C59B27]" : "bg-[#EADFCB]"}`} />
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.4 }}
                className="mt-8 rounded-3xl border border-[#EADFCB] bg-white p-8"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">
                  Question {step + 1} / {QUESTIONS.length}
                </p>
                <h2 className="mt-2 font-serif text-2xl font-semibold text-[#1A1714]">{QUESTIONS[step].title}</h2>
                <div className="mt-6 grid gap-3">
                  {QUESTIONS[step].options.map((o) => (
                    <button
                      key={o.label}
                      data-testid={`quiz-option-${QUESTIONS[step].key}-${String(o.v)}`}
                      onClick={() => answer(QUESTIONS[step].key, o.v)}
                      className="rounded-2xl border border-[#EADFCB] bg-[#FAF8F5] px-5 py-4 text-left text-sm font-medium text-[#1A1714] transition-all hover:border-[#C59B27] hover:bg-[#F7EEDB] active:scale-[0.98]"
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        ) : (
          <div className="mt-10">
            <Reveal className="rounded-3xl border border-[#C59B27]/40 bg-white p-8 text-center">
              <Sparkles className="mx-auto text-[#C59B27]" size={36} />
              <h2 className="mt-3 font-serif text-2xl font-semibold text-[#1A1714]">Vos 3 parfums idéaux</h2>
              <p className="mt-1 text-sm text-[#5C5449]">Sélectionnés selon vos réponses, du plus adapté au troisième choix.</p>
            </Reveal>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="quiz-results">
              {results.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.1}>
                  <PerfumeCard p={p} />
                </Reveal>
              ))}
            </div>
            <div className="mt-8 text-center">
              <button
                data-testid="quiz-restart-button"
                onClick={restart}
                className="inline-flex items-center gap-2 rounded-full border border-[#C59B27] px-6 py-3 text-sm font-semibold text-[#C59B27] transition-all hover:bg-[#C59B27] hover:text-white active:scale-95"
              >
                <RotateCcw size={15} /> Refaire le diagnostic
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Quiz;
